import React, { useEffect, useState } from 'react';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetBrokerSsoConfigurationList,
  useGetLandingPageOption,
  useGetTablePreferences,
  usePostBrokerSsoConfiguration,
  usePostTablePreferences,
  useWealthlaneIdentityApi,
} from '../../../hooks';
import { useQueryClient } from 'react-query';
import { ModalStatusType } from '../../../types/common';
import { BrokerSsoConfigurationService } from '../../../services/wealthlane-identity-services/services/BrokerSsoConfigurationService';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { brokerSsoConfigurationListHeader } from '../../../constants/table-headers/brokerSsoConfigurationListHeader';
import Spinners from '../../../components/spinner';
import Button from '../../../components/button';
import { PageHeader } from '../company-profile/profile.styled';
import Heading from '../../../components/typography';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import BrokerageSsoConfigurationModal from './brokerageSsoConfigurationModal';
import InitialValuesForm from '../../../constants/initialValues';
import AlertPopup from '../../../components/alert-popup';
import {
  useDynamicTable,
  DynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import { axiosInstance } from '../../../services/apiInterceptor';
import KeyManagementModal from './keyManagementModal';
import { Form, Formik } from 'formik';
import { BROKER_SSO_CONFIGURATION_VALIDATION } from '../../../utils/validations/validations';
import InputText from '../../../components/input-text/inputText';
import CustomToggle from '../../../components/toggle';
import { BrokerSsoConfigContainer } from './brokerSsoConfig.styled';
import FormSelect from '../../../components/dropdown/formSelect';
import { ActiveOptions } from '../../../constants/common';
import _ from 'lodash';

const initialValues = {
  data: InitialValuesForm.brokerageSsoConfiguration,
  id: '',
};

const BrokerageSSOConfiguration = () => {
  const { handleRequest } = useWealthlaneIdentityApi();
  const [initialData, setInitialData] = useState(initialValues);
  const queryClient = useQueryClient();
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isKeyManagementModalOpen,
    setFalse: closeKeyManagementModal,
    setTrue: openKeyManagementModal,
  } = useBoolean(false);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [modalLoading, setModalLoading] = useState(false);
  const [headerList, setHeaderList] = useState<string[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const { mutate, isLoading } = usePostBrokerSsoConfiguration(
    handleRequest,
    closeModal,
    initialData.id
  );

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'BROKER_SSO_CONFIGURATION_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const { tableInfo } = useDynamicTable();

  const {
    data: brokerSsoConfigurationList,
    status,
    isLoading: brokerSsoConfigurationListLoading,
  } = useGetBrokerSsoConfigurationList(handleRequest);

  const { data: tablePreferences } = useGetTablePreferences(
    handleRequest,
    'Broker SSO Configuration'
  );

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const { data: landingPageOptions, isLoading: landingPageOptionsLoading } =
    useGetLandingPageOption(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Broker SSO Configuration',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const getBrokerSsoConfigurationById = (id: string): any => {
    void handleRequest(
      BrokerSsoConfigurationService.getApiIdentityApiAppBrokerSsoConfiguration1(id)
    )
      .then((data: any) => {
        if (data != null) {
          setInitialData({
            id,
            data: {
              ...data,
              isActive: data?.isActive ? 'Active' : 'Inactive',
            },
          });
        }
      })
      .finally(() => {});
  };

  useEffect(() => {
    if (brokerSsoConfigurationList?.[0]?.id) {
      getBrokerSsoConfigurationById(brokerSsoConfigurationList?.[0]?.id);
    }
  }, [brokerSsoConfigurationList]);

  const onPrimaryClick = (values: Record<string, any>): void => {
    deleteBrokerSsoConfigurationById(customValue);
  };

  const deleteBrokerSsoConfigurationById = (id: string): void => {
    setModalLoading(true);
    handleRequest(BrokerSsoConfigurationService.deleteApiIdentityApiAppBrokerSsoConfiguration(id))
      .then((data: any) => {
        if (data != null) {
          toast.success(
            <ToastComponent label={'Success'} message={'Brokerage SSO Configuration deleted.'} />
          );
          void queryClient.invalidateQueries('getBrokerSsoConfigurationList');
          setModalLoading(false);
          closeConfirmModal();
        }
      })
      .catch(() => {
        setModalLoading(false);
        closeConfirmModal();
      })
      .finally(() => {
        setModalLoading(false);
        closeConfirmModal();
      });
  };

  // Table Actions
  const tableActions = [
    {
      id: 'view',
      action: 'View',
      as: 'View',
      onClick: async (id: string, rowData: Record<string, string>) => {
        setModalStatus('View');
        getBrokerSsoConfigurationById(id);
      },
    },
    {
      id: 'edit',
      action: 'Edit',
      as: 'Edit',
      onClick: async (id: string) => {
        setModalStatus('Edit');
        getBrokerSsoConfigurationById(id);
      },
    },
    {
      id: 'keyManagement',
      action: 'Key Management',
      as: 'Edit',
      onClick: async (id: string, rowData: any) => {
        setSelectedRowData(rowData);
        openKeyManagementModal();
      },
    },
    {
      id: 'delete',
      action: <div className='delete-label'>Delete</div>,
      // as: 'Delete',
      onClick: (id: string) => {
        setModalStatus('Delete');
        setCustomValue(id);
        openConfirmModal();
      },
    },
  ];

  const tableHeaderList = brokerSsoConfigurationListHeader({
    actions: schemaData?.columns,
    tableActions,
  })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const brokerSsoConfigurationHeader = (): any[] => {
    return brokerSsoConfigurationListHeader({ actions: schemaData?.columns, tableActions }).filter(
      (item) => item.isSubHeader === false || headerList.includes(item.accessor)
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: brokerSsoConfigurationHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  const data = React.useMemo(
    () => brokerSsoConfigurationList || [],
    [brokerSsoConfigurationList, brokerSsoConfigurationListLoading]
  );

  // if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  const renderRightToolbarButton = () => {
    return (
      <Button
        as='Create'
        title='Add Broker SSO Configuration'
        variant='primary'
        icon={'add_circle_outline'}
        id='brokerSsoConfigurationAddBrokerageSsoConfigurationButton'
        onClick={() => {
          setModalStatus('Add');
          openModal();
          setInitialData(initialValues);
        }}
      />
    );
  };

  return (
    <div id='brokerageSsoConfigurationPageContainer'>
      <PageHeader id='brokerageSsoConfigurationPageHeader'>
        <Heading
          variant='h3'
          title='Brokerage SSO Configuration'
          id='brokerageSsoConfigurationPageTitle'
        />
        <Heading
          variant='body-s'
          title={`List of brokerage SSO configurations.`}
          id='brokerageSsoConfigurationPageDescription'
        />
      </PageHeader>

      {/* <DynamicTable
        id='commissionDataTable'
        schema={schema}
        data={data}
        loading={status}
        // filterControls={controls}
        totalCounts={brokerSsoConfigurationList?.length ?? 10}
        components={{ renderRightToolbarButton }}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      /> */}
      <BrokerSsoConfigContainer id='brokerSsoConfigContainer'>
        <Formik
          enableReinitialize
          initialValues={initialData.data}
          validationSchema={BROKER_SSO_CONFIGURATION_VALIDATION}
          onSubmit={(values) => {
            mutate(values);
          }}>
          {({ touched, handleChange, errors, values, setFieldValue }) => {
            useEffect(() => {
              if (!values?.configurationData?.landingPage && landingPageOptions?.length) {
                const options = landingPageOptions.find(
                  (option) => option.label === 'Tax Documents'
                );
                if (options) {
                  setFieldValue('configurationData.landingPage', options.value);
                }
              }
            }, [landingPageOptions, values?.configurationData?.landingPage]);

            return (
              <Form id='brokerageSsoConfigurationForm'>
                <div id='modalBody'>
                  <div className='grid grid-cols-3 gap-4'>
                    <InputText
                      type='text'
                      label='Name'
                      name='name'
                      id='brokerSsoConfigName'
                      className='col-span-2'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      required
                    />
                    <FormSelect
                      name={'isActive'}
                      id='isActive'
                      label='Status'
                      viewName='isActive'
                      containerClassName='formGroup'
                      setFieldValue={setFieldValue}
                      onChange={handleChange}
                      placeholder={'Select'}
                      options={ActiveOptions}
                      touched={touched}
                      errors={errors}
                      values={values}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      required
                    />
                  </div>

                  <div className='mt-4'>
                    <div className='grid grid-cols-2 gap-4'>
                      <InputText
                        type='text'
                        label='Issuer'
                        name='configurationData.issuer'
                        id='brokerSsoConfigIssuer'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                      <InputText
                        type='text'
                        label='Audience'
                        name='configurationData.audience'
                        id='brokerSsoConfigAudience'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                      <InputText
                        type='text'
                        label='ACS URL'
                        name='configurationData.acsUrl'
                        id='brokerSsoConfigAcsUrl'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                      <InputText
                        type='text'
                        label='Profile'
                        name='configurationData.profile'
                        id='brokerSsoConfigProfile'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                      />
                      <FormSelect
                        name={'configurationData.landingPage'}
                        id='brokerSsoConfigLandingPage.landingPage'
                        label='Landing Page'
                        viewName='configurationData.landingPage'
                        containerClassName='formGroup'
                        setFieldValue={setFieldValue}
                        onChange={handleChange}
                        placeholder={'Select'}
                        isLoading={landingPageOptionsLoading}
                        options={landingPageOptions}
                        touched={touched}
                        errors={errors}
                        values={values?.configurationData?.landingPage}
                        arrValue={values?.configurationData?.landingPage}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                    </div>
                  </div>

                  <div className='flex justify-end gap-x-5 mt-8' id='formActions'>
                    <Button
                      title='Key Management'
                      type='button'
                      loading={false}
                      id='keyManagementButton'
                      onClick={() => {
                        openKeyManagementModal();
                      }}
                      variant='primary-outline'
                      icon='key'
                    />
                    <Button
                      title='Save'
                      type='button'
                      loading={isLoading}
                      id='saveButton'
                      disabled={!_.isEmpty(errors) || _.isEqual(values, initialData.data)}
                      onClick={() => {
                        const payload = {
                          ...values,
                          isActive: values.isActive === 'Active' ? true : false,
                        };
                        mutate(payload);
                      }}
                      variant='primary'
                      icon='check_circle'
                    />
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>

        <AnimationWrapper id='brokerageSsoConfigurationAnimationWrapper'>
          {isModalOpen && (
            <BrokerageSsoConfigurationModal
              id='brokerageSsoConfigurationModal'
              isModalOpen={isModalOpen}
              closeModal={closeModal}
              initialData={initialData}
              handleRequest={handleRequest}
              setModalStatus={setModalStatus}
              loading={false}
              modalStatus={modalStatus}
            />
          )}
        </AnimationWrapper>
        <AnimationWrapper id='brokerageSsoConfigurationAnimationWrapper'>
          {isKeyManagementModalOpen && (
            <KeyManagementModal
              id='brokerageSsoConfigurationKeyManagementModal'
              isModalOpen={isKeyManagementModalOpen}
              closeModal={closeKeyManagementModal}
              initialData={initialData}
            />
          )}
        </AnimationWrapper>
        {isConfirmOpen && (
          <AlertPopup
            id='brokerageSsoConfigurationDeleteAlert'
            isShow={isConfirmOpen}
            closeModal={closeConfirmModal}
            modalStatus={modalStatus}
            title={'this Message'}
            handleSubmit={onPrimaryClick}
            // setRemark={setRemark}
            // remark={remark}
            loading={modalLoading}
          />
        )}
      </BrokerSsoConfigContainer>
    </div>
  );
};

export default withDynamicTableContext(BrokerageSSOConfiguration, false, axiosInstance);
